(ns app.front.form.render
  (:require [app.domain.form.model :as form-model]
            [app.domain.form.render-link :as form-link]
            [app.domain.form.validators :as form-validators]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.front.acceptance.subs :as acceptance-subs]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.form.responsive-table :as resp-table]
            [app.front.form.subs :as form-subs]
            [app.front.form.util :as front-form-util]
            [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-all for-all-indexed for-frag for-frag-indexed for-indexed] :as futil]
            [app.front.view.form-util :as form-util]
            [app.front.view.system-of-units.toggle :as sou-toggle]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.domain.system-of-units :as sou]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [goog.string :as gstr]
            [goog.string.format]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-event-fx
  ::open-form-for-edit
  (fn [_ [_ target-id form-id]]
    {:fx [[:dispatch [::form-subs/capture-opening-values form-id]]
          [:dispatch [:app.front.process/signal
                      target-id
                      [:process.signal/open-form-for-edit {:form-id form-id}]
                      {:on-success  [::form-util/update-form-field.success form-id nil]
                       :track-save? true}]]]}))

(rf/reg-event-fx
  ::file-upload-success
  (fn [_ _]
    {:dispatch [:notifications/add :toast :success :success :form/file-added]}))

(rf/reg-event-fx
  ::file-upload-error
  (fn [_ _]
    {:dispatch [:notifications/add :toast :error :error :form/file-added-error]}))

(rf/reg-event-fx
  ::file-delete-error
  (fn [_ _]
    {:dispatch [:notifications/add :toast :error :error :form/file-removed-error]}))

(rf/reg-event-fx
  ::file-delete-success
  (fn [_ _]
    {:dispatch [:notifications/add :toast :success :success :form/file-removed]}))

(rf/reg-event-fx
  ::form-file-uploaded
  (fn [_ [_
          {:keys [target-phase update-field-signal form-id form-field-id field-key]}
          status
          response]]
    (let [file-id (ffirst response)]
      {:fx [[:dispatch [::entities/merge-entity-db status response]]
            [:dispatch [::form-subs/capture-opening-values form-id]]
            [:dispatch [:app.front.process/signal
                        target-phase
                        [(or update-field-signal :process.signal/update-form-data)
                         {:form-id              form-id
                          :field-key            field-key
                          :form-field-id        form-field-id
                          :new-entity-ref-value file-id}]
                        {:on-success  [::form-util/update-form-field.success form-id [::file-upload-success]]
                         :on-error    [::file-upload-error]
                         :track-save? true}]]]})))

(rf/reg-event-db
  ::add-table-row
  (fn [db [_ table row]]
    (update-in db [::table-rows table] #(conj (set %) row))))

(rf/reg-sub ::table-row-added?
  (fn [db [_ table row]]
    (-> (get-in db [::table-rows table])
        (contains? (str row)))))

(rf/reg-sub ::form-field-children
  :<- [::entities/context]
  (fn [ctx [_ parent-id]]
    (if parent-id
      (entities/find-entities ctx {:form.field/parent parent-id})
      [])))

(defn child-of [parent-id id-or-index {:keys [form]}]
  (when (some? id-or-index)
    (let [attribute           (if (integer? id-or-index)
                                :form.field/index
                                :form.field/id)
          fields-under-parent (<sub [::form-field-children parent-id])]
      (if (= attribute :form.field/id)
        (form-model/find-existing-field fields-under-parent
                                        [id-or-index
                                         nil
                                         nil
                                         parent-id
                                         (-> form :form/party :db/id)
                                         (-> form :form/group-config :db/id)])
        ;; if this is a collection field (row or column), index must be unique under the same parent and the parent
        ;; cannot be the phase, only another field, so just check the index.
        (->> fields-under-parent
             (filter #(-> % :form.field/index (= id-or-index))))))))

(defn label-text-silent [{:keys [label]}]
  (form-model/label-text label (<sub [:language])))

(defn label-text [attr]
  (or (label-text-silent attr)
      "Missing :label in element attributes"))

(defn field-updated?
  "If form-field has been updated since the last change-request, according to :application/form-fields"
  [form-id id]
  (<sub [::form-subs/form-field-updated? form-id id]))

(defn updated-class
  [form-id id]
  (when (field-updated? form-id id)
    " updated"))

(defn updated-info-form
  [form-id]
  (<sub [::form-subs/some-form-field-updated? form-id]))

(defn value-change-pill [{:keys [form-id tr preview?]} form-field]
  (when (and (not preview?) (ff/enabled? :feature-flag.id/form-field-change-metadata))
    (let [field-id (:db/id form-field)]
      (when (<sub [::form-subs/form-field-pill-attention? form-id field-id])
        (when-let [{:keys [ts user]} (<sub [::form-subs/form-field-baseline-change form-id field-id])]
          [:div.mt-2
           [ui/small-pill {:kind :attention :uppercase? false}
            (tr [:form-field/baseline-last-changed]
                [((<sub [::date-util/date-time-fn]) ts) user])]])))))

(defn placeholder-text [lang {:keys [placeholder]}]
  (str
    (cond
      (string? placeholder) placeholder
      (map? placeholder)    (i18n-model/get-text-from-entities-or-map placeholder lang)
      :else                 "")))


(defn- attr->id-str [{:keys [field]}]
  (str field))

(defn dom-id
  "Page-unique DOM id. A page can render several forms, role- and work-specific forms repeat the same schema,
  and top-level fields are application children shared by forms, so ids are scoped by the form."
  [{:keys [form-id]} & parts]
  (str/join "-" (into ["form" (or form-id "preview")] parts)))

(defn field-dom-id
  "Schema previews have no form-field entities, so the schema key stands in for the entity id."
  [ctx form-field {:keys [field id]}]
  (dom-id ctx (or (:db/id form-field) (some-> (or field id) futil/sanitize-id))))

;; helper functions for rendering image

(defn- file-src [form-id file-id]
  (str "/api/command/file/download-form-file?form-id="
       form-id
       "&file-id="
       file-id))

(defn- image-tag [ctx {:keys [src class alt]}]
  (let [file-id (->> ctx
                     :form
                     :form/schema
                     :form.schema/files
                     (filter #(-> % :file/filename (= src)))
                     first
                     :db/id)
        form-id (-> ctx :form :db/id)]
    [:img {:class class
           :alt   alt
           :src   (file-src form-id file-id)}]))


(defn diff-tool-classes [attr]
  (when (-> attr :diff) {:class (front-form-util/changes-class (-> attr :diff :type))}))

;;
;; Render form values using provided form schema:
;;


(defmulti
  ^{:arglists '([ctx key-idx current-id schema])}
  render
  "Accepts a context, index, a current entity ID, and a form schema. Returns a DOM hierarchy in hiccup format."
  (fn [_ _ _ schema] (first schema)))


(defn render-form-element [ctx key-idx current-id [_ attr :as schema]]
  (when (<sub [::form-subs/field-visible? attr])
    (let [rendered   (render ctx key-idx current-id schema)
          form-field (when (:field attr)
                       (child-of current-id (:field attr) ctx))]
      ;; Stable `data-guide-id` lets NoVa's form tools point the user at a field on hover — see
      ;; `form-model/field-guide-id`. Highlight ring is drawn on this wrapper (not the inner
      ;; control) since scoping it to Carbon's internal markup would break for hidden/absent
      ;; native inputs (checkbox, radio, div-based dropdowns).
      (if (and rendered (:field attr))
        [:div (p/assoc-if {} :data-guide-id (when (:label attr)
                                              (form-model/field-guide-id (:field attr))))
         rendered
         [value-change-pill ctx form-field]]
        rendered))))


(defmethod render :default [_ _ current-id [element]]
  (throw (ex-info (str "unknown schema element: " element) {:current-id current-id, :element element})))

(defn show-required-as-invalid?
  "Required fields that are blank should show an error, but only after they've been accessed
   (are 'dirty') or if the form has been modified previously."
  [{:keys [form initial?]} field-dirty?]
  (or (and (not initial?) (-> form :latest-change :ts some?))
      field-dirty?))

(defn- form-has-blank-required-fields?
  "Goes recursively through all of the children to find out if there are fields that are required
   but whose value is still blank."
  [ctx current-id children]
  (letfn [(blank-required-field [[_ {:keys [field] :as attr} & children]]
            (when-let [field-entity (child-of current-id field ctx)]
              (or (and (str/blank? (:form.field/value field-entity))
                       (:form.field/required attr))
                  (when children
                    (some blank-required-field children)))))]
    (some blank-required-field children)))

(defn- form-has-required-fields?
  "Goes recursively through all of the children to find out if there are fields that are required."
  [children]
  (letfn [(required-field [[_ {:form.field/keys [required]} & children]]
            (or required (some required-field children)))]
    (some required-field children)))

(defn- form-has-unit-attribute-field?
  "Goes recursively through all of the children to find out if there is `:unit` attribute for any field."
  [children]
  (letfn [(unit-field [[_ {:keys [unit]} & children]]
            (or unit (some unit-field children)))]
    (some unit-field children)))


(defn open-form-for-edit [{:keys [target-id form-id tr]}]
  (when (<sub [:app.front.process/signal-permitted?
               target-id
               [:process.signal/open-form-for-edit
                {:form-id form-id}]])
    [:div
     [ui/modal-confirmation-button
      {:tr                  tr
       :id                  "open-for-edit"
       :on-yes-click        #(>evt [::open-form-for-edit target-id form-id])
       :trigger-button-text (tr [:edit-form])
       :trigger-button-opts {:kind :tertiary
                             :icon i/pen}
       :modal-title         (tr [:signature/open-form-for-edit-title])}
      (tr [:signature/open-form-for-edit-info])]]))


(defn form-status [tr form]
  (let [acceptance (<sub [::acceptance-subs/entity-latest-acceptance (:db/id form)])
        status     (<sub [::form-subs/form-status-color-code (:db/id form)])
        label      (cond
                     (<sub [::form-subs/form-missing-mandatory-fields? (:db/id form)])
                     (tr [:missing-mandatory-fields])

                     (= status :attention)
                     (tr [:wizard/forms-confirmation-needed])

                     :else
                     (some-> acceptance :acceptance/customer-value vector tr))]
    [:div.flex.flex-row.justify-start.items-center.gap-4
     (when label [ui/small-pill {:kind (if (= status :attention) :attention :secondary)} label])
     [:span.font-bold
      {:class "py-0.5"}
      (cond
        (-> acceptance :acceptance/value some?)
        (tr [(:acceptance/value acceptance)])
        (and (:domain/state form) (-> form :latest-change some?))
        (tr [(:domain/state form)])
        :else
        nil)]]))

(defn form-component [{:keys [tr form-id disabled? preview? form lang inline? wizard?] :as ctx} _ current-id [_ _ & children]]
  (let [status (<sub [::form-subs/form-status-color-code form-id])]
    (into
      [:form {:key   (str form-id "-root")
              :class [(if inline?
                        "my-8"
                        "bg-ui-white p-8 mb-8 rounded")
                      (when disabled? "read-only")]}
       (when-not wizard?
         [:div.flex.flex-col.lg:flex-row.items-start.lg:items-center.mb-6.gap-4
          [:div.flex-1
           [ui/heading-with-status
            {:status status}
            [form-model/form-label form lang]]]
          [form-status tr form]])

       [:div.flex.flex-col.lg:flex-row-reverse.lg:items-start
        (when (form-has-unit-attribute-field? children)
          [:div.-my-1
           [sou-toggle/system-of-units-toggle]])
        [:div.flex-1.mt-6.lg:mt-0
         (when disabled?
           [ui/help (tr [:form/read-only-help]) {:class "p-4 mb-6"}])
         (if (form-has-required-fields? children)
           [:div.flex-1.required-field.mt-4.lg:mt-3.self-end.lg:self-center
            (tr [:form-required-guide])]
           [:div.flex-1])]]
       (when-not preview?
         [:div.flex.flex-col.lg:flex-row
          (cond
            (<sub [::form-subs/field-changes-initial-loading-for-form? form-id])
            [:div.bx--col-xs-12.my-2 (cc/loading-spinner {:with-overlay false :small true})]
            (updated-info-form form-id)
            [:div.bx--col-xs-12.changes.my-2 (tr [:changes])])])
       (when (and (show-required-as-invalid? ctx false)
                  (form-has-blank-required-fields? ctx current-id children))
         [:div.bx--row.form-page__notification
          [cc/inline-notification {:kind     :error
                                   :title    (tr [:fill-required-guide])
                                   :subtitle ""}]])]
      (for-all-indexed [[i child] children]
        [render-form-element ctx i current-id child]))))


(defmethod render :form [ctx key-idx current-id schema]
  (let [auth-group       (-> ctx :form group-model/get-authority-group)
        phrases          (<sub [::entities/entities {:entity/type            :entity.type/phrase
                                                     :domain/authority-group (:db/id auth-group)}])
        default-category (-> auth-group :group/phrase-category-inspections :db/id)
        ctx              (assoc ctx
                                :phrases phrases
                                :default-category-for-phrases default-category
                                :auth-group auth-group)]
    [form-component ctx key-idx current-id schema]))


(defn- grid-classes [max-elements]
  (let [prefix "bx--col-"
        sizes  {:xs 12
                :sm (if (> max-elements 1) 6 12)
                :md (int (/ 12 max-elements))}]
    (->> sizes
         (map (fn [[s cols]]
                (str prefix (name s) "-" cols)))
         (str/join " "))))


(defmethod render :section [ctx _key-idx current-id [_ attr & children]]
  (when (seq children)
    (into
      [:div.border-b.border-ui-dark-gray.border-solid.last:border-b-0.pt-6.pb-2.text-ui-night-sky-100
       (when (:label attr) [:h4 (label-text attr)])]
      (for-all-indexed [[i child] children]
        [render-form-element ctx i current-id child]))))


(defmethod render :subsection [{:keys [inline?] :as ctx} key-idx current-id [_ attr & children]]
  (when (seq children)
    (let [section-id (str current-id "_" (:form-id ctx) "_" key-idx)]
      (into
        (into [:div.mt-6 {:id section-id}
               (when (:label attr) [(if inline? :h5.mb-2 :h4.mb-3) (label-text attr)])])
        (for-all-indexed [[i child] children]
          [render-form-element ctx i current-id child])))))


(defmethod render :ui-only [ctx _ current-id [_ _ & children]]
  (into
    [:div]
    (for-all-indexed [[i child] children]
      [render-form-element ctx i current-id child])))


(defmethod render :print-only [_ & _]
  [:div])


(defmethod render :form-row [ctx key-idx current-id [_ {:keys [number-of-columns class-name diff]} & children]]
  (let [key               (str current-id "/form-row:" key-idx)
        children-to-show  (->> children
                               (remove #(and number-of-columns
                                             (not (<sub [::form-subs/field-visible? (second %)]))))
                               doall)
        rendered-children (->> children-to-show
                               (map-indexed (fn [i child]
                                              (let [child [render-form-element ctx i current-id child]]
                                                [:div {:class (grid-classes (if (pos-int? number-of-columns)
                                                                              number-of-columns
                                                                              (count children)))
                                                       :key   (str key "/row:" i)}
                                                 child])))
                               doall
                               (remove nil?))]
    (when (seq rendered-children)
      [:div.bx--row.form-page__row
       {:key   key
        :class [(when class-name class-name)
                (when diff (front-form-util/changes-class (-> diff :type)))]}
       rendered-children])))

(defn- update-signal-args
  [{:keys [form-id parent-field-id]} form-field field-key value]
  (p/assoc-if {:form-id       form-id
               :field-key     field-key
               :form-field-id (:db/id form-field)
               :new-value     value}
              :parent-field-id parent-field-id))

(defn- input-change-fn [{:keys [target-id field-key form-field] :as ctx} & [keywordize?]]
  (fn [e]
    (>evt [::form-util/update-form-field
           target-id
           (update-signal-args ctx
                               form-field
                               field-key
                               (cond-> (futil/tval e)
                                 keywordize? keyword))])))


(defn- parse-number [s step]
  (if (and (number? step) (< step 1))
    (let [n (js/parseFloat s)]
      (when-not (js/isNaN n)
        n))
    (futil/parse-int s)))


(defn number-input
  "Custom number input to avoid the arrow buttons in Carbon NumberInput, as those expect the value property
   to be mutable."
  [{:keys [id form-id form-field-id label-text invalid invalid-text unit-label step hide-label *input-dom required
           wrapper-class]
    :as   props}]
  [:div.bx--form-item
   {:class [(when (field-updated? form-id form-field-id)
                  ; Updated-class that will effect on label
              "updated")
            wrapper-class]}
   [:div.form-page__input.form-page__number-input.bx--number
    (when invalid
      {:data-invalid invalid})
    (when-not hide-label
      [:label.bx--label {:for   id
                         :class (when required "required-field")} label-text])
    [:div.number-input-wrapper
     [:input (merge ui/number-input-props
                    {:id   id
                     :step (or step 1)
                     :ref  (fn [ref]
                             (ui/ref-prevent-wheel ref)
                             (when-not (nil? *input-dom)
                               (reset! *input-dom ref)))}
                    (select-keys props [:value :on-change :class-name :on-blur :on-key-up :placeholder
                                        :default-value :disabled :min :required]))]
     (when-not (str/blank? unit-label)
       [:div.input-unit unit-label])]
    (when-not (str/blank? invalid-text)
      [:div.bx--form-requirement.number-input-requirement
       invalid-text])]])


(defn file-controls [{:keys [form-field target-id form-id update-field-signal tr field-key] :as ctx}
                     parent-attr
                     latest-version?]
  (when-let [file-entity (:form.field/entity-ref form-field)]
    [:<>
     [:div.flex.items-center.mt-2
      (cc/icon {:icon        i/undersign
                :fill        (if latest-version? "green" "gray")
                :description (tr [:form/uploaded-by])})
      [:div.ml-2
       [:p.p-0.leading-tight
        (tr [:form/uploaded-by])
        [:span.font-bold.mx-1 (user-model/full-name (:entity/creator file-entity))]
        ((<sub [::date-util/date-time-fn]) (:entity/created file-entity))]]
      [:div.flex.flex-row
       [:div.relative.ml-2 {:style {:top "1px"}}
        [file-download/download-workspace-file-link file-entity
         {}]]
       [:div.ml-4.align-bottom {:style {:margin-top "auto"}}
        (when-not (form-util/update-form-disabled? (assoc ctx :delete-field? true) parent-attr)
          [ui/modal-confirmation-button
           {:tr                  tr
            :id                  "remove-signature"
            :on-yes-click        #(do
                                    (>evt [::form-subs/capture-opening-values form-id])
                                    (>evt [:app.front.process/signal
                                           target-id
                                           [(or update-field-signal :process.signal/update-form-data)
                                            {:form-id       form-id
                                             :form-field-id (:db/id form-field)
                                             :field-key     field-key
                                             :delete-field? true}]
                                           {:on-success  [::form-util/update-form-field.success
                                                          form-id
                                                          [::file-delete-success]]
                                            :on-error    [::file-delete-error]
                                            :track-save? true}]))
            :text-button?        true
            :trigger-button-text (tr [:form/file-remove])}
           [:div.flex.flex-row
            [:p.bx--modal-content__text
             (tr [:form/remove-confirmation])]]])]]]]))


(defmethod render :file-input [{:keys [workspace-id target-id form-id update-field-signal tr] :as ctx}
                               _ current-id [_ attr]]
  (p/if-all-let [form-field    (child-of current-id (-> attr :field) ctx)
                 form-field-id (:db/id form-field)
                 _             (<sub [::form-subs/field-visible? attr])]
    (let [child-fields   (->> (:entity/_parent form-field)
                              (sort-by :form.field/index))
          latest-version (last child-fields)
          application    (<sub [::entities/entity target-id])]
      [:div.mt-1
       [:div.flex.flex-col
        (for-frag [child-field child-fields]
          [file-controls
           (assoc ctx :form-field child-field :field-key (:form.field/id form-field))
           attr
           (= child-field latest-version)])]
       [:div.flex.flex-row.justify-between
        (when latest-version
          [:div.my-2.mr-4
           [file-download/download-workspace-file-button
            (:form.field/entity-ref latest-version)
            {:text (or (:download-button-label attr)
                       (tr [:form/download-uploaded]))}]])
        (when-not (form-util/update-form-disabled? (assoc ctx :form-field form-field) attr)
          [:div.my-2
           [upload/upload-button
            (dom-id ctx "file-upload" form-field-id)
            {:attach-file-fn (fn [file-id filename content-type md5]
                               (comm/send-command-execution
                                 {:command    :file/add-workspace-file
                                  :data       {:workspace-id      workspace-id
                                               :file-id           file-id
                                               :file/filename     filename
                                               :file/content-type content-type
                                               :file/category     :file.category/form-file
                                               :file/md5          md5
                                               :convert-to-pdfa?  true}
                                  :on-success [::form-file-uploaded (util/mmap {:field-key    (:field attr)
                                                                                :target-phase application}
                                                                               update-field-signal
                                                                               form-id
                                                                               form-field-id)]}))
             :label-text     (:upload-button-label attr)
             :accept-types   [".pdf" "application/pdf"]}]])]])))


(defn get-invalid-text [ctx attr value visited]
  (let [validator-fn (when-let [validator (-> attr :form.field/validator)]
                       (form-validators/checker validator))]
    (cond
      (and (not (form-model/field-value-is-set? {:form.field/value value}))
           (-> attr :form.field/required)
           (show-required-as-invalid? ctx visited))
      ((:tr ctx) [:required-field])

      (and validator-fn
           (not (str/blank? value)))
      (some-> (validator-fn value)
              form-validators/string-error))))

(defn disabled-content [{:keys [id hide-label? attr value rich-text?]}]
  [:<>
   (when (not hide-label?) [:label.bx--label.bx--label--disabled (label-text attr)])
   [:div.py-4.whitespace-pre-line
    (if rich-text?
      [ui-rt/editor-legacy {:initial-value value
                            :id            id}]
      value)]])

(defn render-text-input [ctx]
  [(if (:textarea? ctx) cc/text-area cc/text-input)
   (dissoc ctx :form-id :form-field-id :tr :phrases :default-category :textarea? :rich-text?)])

(defn render-rich-text-editor [{:keys [id label-text default-value on-change on-blur phrases auth-group disabled placeholder]}]
  [:div.textarea-with-phrases-wrapper
   (let [editor-params (merge {:id            id
                               :label-text    label-text
                               :initial-value default-value
                               :placeholder   placeholder}
                              (when-not disabled
                                {:on-change          on-change
                                 :on-blur            on-blur
                                 :phrases            phrases
                                 :authority-group-id (:db/id auth-group)}))]
     [ui-rt/editor-legacy editor-params])])

(defn input-with-phrases [{:keys [disabled rich-text? phrases] :as ctx}]
  (cond
    disabled
    (if (or rich-text? phrases)
      (render-rich-text-editor ctx)
      (render-text-input ctx))

    rich-text?
    (render-rich-text-editor ctx)

    phrases
    (render-rich-text-editor ctx)

    :else
    (render-text-input ctx)))


(defn input [{:keys [form-field form-id textarea? target-id inline? tr lang] :as ctx} attr]
  (r/with-let [*value    (r/atom (if (:form.field/value form-field)
                                   (:form.field/value form-field)
                                   (when (some? (:default-value attr))
                                     (:default-value attr))))
               *visited? (r/atom false)]
    (let [form-field-id (-> form-field :db/id)
          input-id      (field-dom-id ctx form-field attr)
          number?       (= (:type attr) :number)
          phrases       (and (:phrases-picker? attr)
                             (seq (:phrases ctx)))
          rich-text?    (:rich-text? attr)
          checker       (when-let [validator (-> attr :form.field/validator)]
                          (form-validators/checker validator))
          component-fn  (cond
                          number? number-input
                          :else input-with-phrases)
          update-field! (fn []
                          (let [old-value (-> form-field :form.field/value)
                                new-value @*value]
                            (when (and (not= (str-util/blank->nil new-value)
                                             (str-util/blank->nil old-value))
                                       (or (nil? checker)
                                           (nil? (checker new-value))))
                              (>evt [::form-util/update-form-field
                                     target-id
                                     (update-signal-args ctx form-field (:field attr) new-value)]))))
          on-change     (fn [e]
                          (let [v (cond-> e
                                    (instance? js/Object e) (-> .-target .-value)
                                    number? (parse-number (:step attr)))]
                            (reset! *value v)))
          on-blur       (fn [_]
                          (reset! *visited? true)
                          (update-field!))
          on-key-up     (fn [e]
                          (case (-> e .-key)
                            "Enter" (if textarea?
                                      e
                                      (update-field!))
                            "Escape" (do
                                       (->> form-field :form.field/value (reset! *value))
                                       (-> js/document
                                           (.getElementById input-id)
                                           .-value
                                           (set! (:form.field/value form-field))))
                            nil))
          invalid-text  (get-invalid-text ctx attr @*value @*visited?)
          invalid?      (boolean invalid-text)
          required?     (-> attr :form.field/required true?)
          value         (if (and (str/blank? @*value)
                                 (some? (:default-value attr)))
                          (:default-value attr)
                          @*value)
          disabled      (form-util/update-form-disabled? ctx attr)
          hide-label?   (true? (or (:table-cell? ctx) (:hide-label attr)))]
      [:div
       {:class [(when (field-updated? form-id form-field-id) "updated")
                (when (-> attr :diff) (front-form-util/changes-class (-> attr :diff :type)))]}
       (if (and inline?
                disabled)
         [disabled-content (util/mmap {:id input-id} hide-label? attr value rich-text?)]
         [component-fn (p/assoc-if {:id            input-id
                                    :form-id       form-id
                                    :form-field-id form-field-id
                                    :default-value value
                                    :class-name    (if (some? (:class-name attr))
                                                     (:class-name attr)
                                                     "form-page__input")
                                    :on-change     on-change
                                    :on-blur       on-blur
                                    :on-key-up     on-key-up
                                    :label-text    (label-text attr)
                                    :hide-label    hide-label?
                                    :invalid       invalid?
                                    :required      required?
                                    :invalid-text  (or invalid-text "")
                                    :placeholder   (placeholder-text lang attr)
                                    :disabled      disabled
                                    :rich-text?    rich-text?}
                                   :unit-label (:unit-label attr)
                                   :step (:step attr)
                                   :phrases phrases
                                   :default-category (and phrases (:default-category-for-phrases ctx))
                                   :tr (and phrases tr)
                                   :textarea? (or textarea? nil))])])))


(defn system-of-unit-number-input [{:keys [target-id form-field tr lang] :as ctx}
                                   {:form.field/keys [validator required]
                                    :keys            [unit default-value class-name step on-change hide-label unit-label
                                                      signal-opts]
                                    :as              attr}]
  (r/with-let [*value    (r/atom (when-let [value (if (not (str/blank? (:form.field/value form-field)))
                                                    (:form.field/value form-field)
                                                    default-value)]
                                   {:numeric value :text (str value)}))
               *visited? (r/atom false)]
    (let [form-field-id        (:db/id form-field)
          checker              (when validator
                                 (form-validators/checker validator))
          update-field!        (fn []
                                 (let [old-value            (:form.field/value form-field)
                                       {new-value :numeric} @*value]
                                   (when (and (not= (str-util/blank->nil new-value)
                                                    (str-util/blank->nil old-value))
                                              (or (nil? checker)
                                                  (nil? (checker new-value))))
                                     (>evt [::form-util/update-form-field
                                            target-id
                                            (update-signal-args ctx form-field (:field attr) new-value)
                                            signal-opts]))))
          on-blur              (fn [_]
                                 (reset! *visited? true)
                                 (update-field!))
          on-key-up            (fn [e]
                                 (case (-> e .-key)
                                   "Enter" (update-field!)
                                   "Escape" (let [value (:form.field/value form-field)]
                                              (reset! *value value))
                                   nil))
          {num-value  :numeric
           text-value :text}   @*value
          invalid-text         (cond
                                 (and (nil? num-value)
                                      required
                                      (show-required-as-invalid? ctx @*visited?))
                                 (tr [:required-field])

                                 (and checker
                                      (some? num-value))
                                 (some-> (checker num-value)
                                         form-validators/string-error))
          invalid?             (boolean invalid-text)
          display-value        (when num-value
                                 (let [converted-num (->> (<sub [::sou-toggle/value-in-selected-system unit num-value])
                                                          (sou/round sou/default-precision))]
                                   (if (== converted-num (parse-number text-value step))
                                     ;; Preserve original user input if converted number is still equal
                                     text-value
                                     converted-num)))
          unit-in-sel          (<sub [::sou-toggle/unit-in-selected-system unit])
          on-change            (fn [e]
                                 (let [text-value (futil/tval e)
                                       num-value  (parse-number text-value step)
                                       ;; This is always in default units (SI in Canada)
                                       conv-value (sou/convert-raw unit-in-sel
                                                                   (-> sou/units unit :system-of-units)
                                                                   num-value)]
                                   ;; Note that at least on Chromium and Safari an invalid number in a number input will
                                   ;; result in a blank string here, so you cannot distinguish between empty and erroneous text
                                   (reset! *value {:text    text-value
                                                   :numeric conv-value})
                                   (when on-change
                                     (on-change num-value))))]
      [number-input (p/assoc-if {:id            (field-dom-id ctx form-field attr)
                                 :form-id       (:form-id ctx)
                                 :form-field-id form-field-id
                                 :value         display-value
                                 :class-name    (or class-name "form-page__input")
                                 :wrapper-class (when (-> attr :diff)
                                                  (front-form-util/changes-class (-> attr :diff :type)))
                                 :on-change     on-change
                                 :on-blur       on-blur
                                 :on-key-up     on-key-up
                                 :label-text    (label-text attr)
                                 :hide-label    (true? (or (:table-cell? ctx) hide-label))
                                 :invalid       invalid?
                                 :invalid-text  (or invalid-text "")
                                 :placeholder   (placeholder-text lang attr)
                                 :disabled      (form-util/update-form-disabled? ctx attr)
                                 :required      required}
                                :unit-label (or unit-label
                                                (<sub [::sou-toggle/label-in-selected-system unit]))
                                :step step)])))


(defmethod render :input [ctx _ current-id [_ attr]]
  (let [form-field (child-of current-id (-> attr :field) ctx)]
    [:div
     ; Updated-class that will effect on labels of number-input-fields
     (when (field-updated? (:form-id ctx) (:db/id form-field))
       {:class "updated"})
     (if (and (= (:type attr) :number)
              (:unit attr))
       [system-of-unit-number-input (assoc ctx :form-field form-field) attr]
       (if (-> attr :form.field/required)
         [:div.required-input [input (assoc ctx :form-field form-field :current-id current-id) attr]]
         [input (assoc ctx :form-field form-field :current-id current-id) attr]))]))


(defn- coll-of-pos-numbers? [coll]
  (every? (every-pred number? pos?)
          coll))


(defn calculate-area-ratio [area-of-wsg area-of-walls]
  (when (coll-of-pos-numbers? [area-of-wsg area-of-walls])
    (* (/ area-of-wsg area-of-walls)
       100)))


(defn- find-value-by-field-id [parent-id field-id ctx]
  (-> (child-of parent-id field-id ctx)
      :form.field/value))


(defn- resolve-area-ratio [ctx current-field value current-id]
  ;; Due to a fixed field key typo the value may be in either of these fields depending on form schema.
  (let [area-of-wsg-val   (if ({:project-design-conditions.area-of-walls-skylights-and-glass
                                :project-design-conditions.area-of-windows-skylights-and-glass} current-field)
                            value
                            (or (find-value-by-field-id current-id
                                                        :project-design-conditions.area-of-windows-skylights-and-glass
                                                        ctx)
                                (find-value-by-field-id current-id
                                                        :project-design-conditions.area-of-walls-skylights-and-glass
                                                        ctx)))
        area-of-walls-val (if (= current-field :project-design-conditions.area-of-walls)
                            value
                            (find-value-by-field-id current-id
                                                    :project-design-conditions.area-of-walls
                                                    ctx))]
    (calculate-area-ratio area-of-wsg-val area-of-walls-val)))

(rf/reg-event-fx ::area-input-updated
  (fn [{:keys [db]} [_ {:keys [form-id field-id current-id target-id] :as ctx} _status _entities]]
    (let [ent-ctx    (entities/db->context db)
          form       (entities/find-entity ent-ctx form-id)
          field      (entities/find-entity ent-ctx field-id)
          render-ctx {:form form}
          ratio      (resolve-area-ratio render-ctx
                                         (:form.field/id field)
                                         (:form.field/value field)
                                         current-id)]
      (when (and (number? ratio) (pos? ratio))
        (let [ratio-field (child-of current-id :project-design-conditions.area-ratio render-ctx)]
          {:fx [[:dispatch [::form-util/update-form-field
                            target-id
                            (update-signal-args ctx ratio-field (:form.field/id ratio-field) ratio)]]]})))))

(defmethod render :area-input [{:keys [preview?] :as ctx} _ current-id [_ attr]]
  (let [form-field (child-of current-id (-> attr :field) ctx)]
    (when (or preview?
              (and form-field (<sub [::form-subs/field-visible? attr])))
      (let [target-id   (:target-id ctx)
            signal-opts {:on-success [::area-input-updated
                                      {:form-id    (:form-id ctx)
                                       :field-id   (:db/id form-field)
                                       :current-id current-id
                                       :target-id  target-id}]}]
        [:div
         (diff-tool-classes attr)
         [system-of-unit-number-input (assoc ctx :form-field form-field) (assoc attr :signal-opts signal-opts)]]))))


(defmethod render :area-ratio [ctx _ current-id [_ attr]]
  (let [form-field (child-of current-id (-> attr :field) ctx)
        input-id   (field-dom-id ctx form-field attr)
        ratio      (:form.field/value form-field)]
    [:div.bx--form-item
     (diff-tool-classes attr)
     [:div.form-page__input.form-page__number-input.bx--number
      [:label.bx--label {:for input-id} (:label attr)]
      [:div.number-input-wrapper
       [:input {:key           (str (:id attr) (:form.field/value form-field))
                :id            input-id
                :type          :number
                :disabled      true
                :class-name    "form-page__input"
                :default-value (if ratio
                                 (gstr/format "%.2f" ratio)
                                 "")}]
       [:div.input-unit "%"]]]]))


(defmethod render :textarea [ctx _ current-id [_ attr]]
  (let [form-field (child-of current-id (-> attr :field) ctx)]
    (if (-> attr :form.field/required)
      [:div.required-input [input (assoc ctx :form-field form-field :textarea? true) attr]]
      [input (assoc ctx :form-field form-field :textarea? true) attr])))


(defn select [{:keys [form-field tr] :as ctx} {:form.field/keys [valid-values] :as attr}]
  (let [current-value (:form.field/value form-field)
        options       (->> valid-values
                           (concat (when (and (not (str/blank? current-value))
                                              (not (contains? (set valid-values) current-value)))
                                     [current-value]))
                           (map (fn [v]
                                  [v (if (:no-tr? attr) v (tr [(keyword (-> attr :field name) v) (keyword v) v]))]))
                           (#(if (:no-sorting? attr) % (sort-by second %))))
        required?     (-> attr :form.field/required true?)
        invalid?      (boolean (and required?
                                    (or (str/blank? current-value)
                                        (and (keyword? current-value) (= "none" (name current-value))))
                                    (show-required-as-invalid? ctx false)))]
    [:div
     (diff-tool-classes attr)
     (into
       (cc/select
         {:id            (field-dom-id ctx form-field attr)
          :label-text    (label-text attr)
          :hide-label    (true? (or (= :block-select (:render attr)) (:table-cell? ctx) (:hide-label attr)))
          :class-name    (str "full-width-select" (updated-class (:form-id ctx) (:db/id form-field)))
          :on-change     (input-change-fn (assoc ctx :field-key (:field attr)))
          :invalid       invalid?
          :invalid-text  (if invalid? (tr [:required-field]) "")
          :default-value (or current-value "")
          :disabled      (form-util/update-form-disabled? ctx attr)
          :required      required?}
         (when (or (str/blank? current-value)
                   (not required?))
           [ui/placeholder-select-item tr ""]))
       (for [[value text] options]
         (cc/select-item {:text  (str text)
                          :value value})))]))


(defmethod render :select [ctx _ current-id [_ attr]]
  (let [form-field (child-of current-id (-> attr :field) ctx)]
    (if (= :block-select (:render attr))
      [:div.bx--row.form-page__block-select
       {:key (attr->id-str attr)}
       [:div.bx--col-xs-12.bx--col-sm-7.bx--col-md-8.bx--col-lg-9.bx--col-xl-7.bx--col-xxl-6
        [:p {:class (when (-> attr :form.field/required) "required-field")} (label-text attr)]]
       [:div.bx--col-xs-12.bx--col-sm-5.bx--col-md-4.bx--col-lg-3.bx--col-xxl-2
        [select (assoc ctx :form-field form-field) attr]]]
      (if (-> attr :form.field/required)
        [:div.required-input [select (assoc ctx :form-field form-field) attr]]
        [select (assoc ctx :form-field form-field) attr]))))


(defmethod render :checkbox-group [ctx key-idx current-id [_ attr & children]]
  (let [children (->> (for-all-indexed [[i checkbox] children]
                        ^{:key (str "checkbox-" key-idx i)}
                        [render-form-element ctx i current-id checkbox])
                      (remove nil?)
                      seq)
        layout   (or (:layout attr) :vertical)]
    (when children
      [:div
       (diff-tool-classes attr)
       [:fieldset.bx--fieldset
        {:key (str key-idx)}
        (when-not (:hide-label attr)
          [:legend.bx--label
           {:class (when (-> attr :form.field/required) "required-field")}
           (label-text attr)])
        (if (= :horizontal layout)
          [:div.bx--row
           (for-frag [c children]
             [:div {:class (grid-classes 3)}
              c])]
          [:<> children])]])))


(defmethod render :checkbox [ctx _ current-id [_ attr]]
  (let [form-field (child-of current-id (:field attr) ctx)
        id-str     (field-dom-id ctx form-field attr)]
    [:div
     (diff-tool-classes attr)
     [cc/checkbox {:key                id-str
                   :id                 id-str
                   :hide-label         (boolean (:hide-label attr))
                   :label-text         (label-text attr)
                   :wrapper-class-name "inline-block"
                   :class-name         (futil/cs (:class-name attr)
                                                 "whitespace-normal"
                                                 (updated-class (:form-id ctx) (:db/id form-field)))
                   :default-checked    (true? (:form.field/value form-field))
                   :disabled           (form-util/update-form-disabled? (assoc ctx :form-field form-field) attr)
                   :on-change          #(>evt [::form-util/update-form-field
                                               (:target-id ctx)
                                               (update-signal-args ctx form-field (:field attr) %)])}]
     (when (:form.field/required attr)
       [:span.required-field.mr-2])]))


(defn remove-table-row-button [{:keys [tr] :as ctx} parent-attr form-table-row]
  [ui/modal-confirmation-button
   {:id                  (str "delete-table-row" (:db/id form-table-row))
    :tr                  tr
    :modal-title         (tr [:form/delete-table-row])
    :trigger-button-text (tr [:form/delete-table-row])
    :disabled?           (form-util/update-form-disabled? (assoc ctx :delete-field? true)
                                                          parent-attr)
    :trigger-button-opts {:icon          i/trash
                          :icon-position :right
                          :size          :small
                          :kind          :tertiary
                          :class-name    "mt-3"}
    :on-yes-click        #(>evt [::form-util/update-form-field
                                 (:target-id ctx)
                                 {:form-id       (:form-id ctx)
                                  :form-field-id (:db/id form-table-row)
                                  :field-key     (-> parent-attr :field)
                                  :delete-field? true}])}
   (tr [:form/delete-table-row-confirmation])])


(def ^:private preview-table-row-count 2)

(defmethod render :table [{:keys [preview?] :as ctx} _ current-id [_ attr & children]]
  (let [field      (child-of current-id (-> attr :field) ctx)
        tr         (:tr ctx)
        rows       (->> (<sub [::form-field-children (-> field :db/id)])
                        (sort-by :form.field/index))
        class      (-> attr :class)
        headers    (-> attr
                       :headers
                       vec
                       (conj [:div {:style {:display "none"}} (tr [:form/delete-table-row])]))
        col-count  (count headers)
        breakpoint (resp-table/get-breakpoint-by-col-count col-count)
        table-opts (util/mmap breakpoint col-count)
        cells      (-> children
                       vec
                       (conj [:remove-row-btn {}]))]

    (when (or (<sub [::form-subs/field-visible? attr])
              preview?)
      [:div.mt-6
       (diff-tool-classes attr)
       [resp-table/table (assoc table-opts :class class)
        [resp-table/header (assoc table-opts :cells headers)]
        (if preview?
          (for-all [row-idx (range preview-table-row-count)]
            ^{:key (str "preview-table-row-" row-idx)}
            [resp-table/row (merge table-opts
                                   {:headers   headers
                                    :render-fn (fn [[tag :as cell]]
                                                 (if (= tag :remove-row-btn)
                                                   (cc/button {:icon             i/trash
                                                               :icon-description (tr [:form/delete-table-row])
                                                               :icon-position    :right
                                                               :size             :small
                                                               :kind             :tertiary
                                                               :class-name       "mt-3"
                                                               :disabled         true
                                                               :on-click         (fn [_])}
                                                              (tr [:form/delete-table-row]))
                                                   (render-form-element
                                                     (assoc ctx :table-cell? true)
                                                     (str "preview-" row-idx)
                                                     current-id
                                                     cell)))
                                    :cells     cells})])
          (for-all [row rows]
            ^{:key (str "table-row-" (:db/id row))}
            [resp-table/row (merge table-opts
                                   {:headers   headers
                                    :render-fn (fn [[tag :as cell]]
                                                 (if (= tag :remove-row-btn)
                                                   (remove-table-row-button ctx attr row)
                                                   (render-form-element
                                                     (assoc ctx :table-cell? true :parent-field-id (:db/id row))
                                                     ""
                                                     (:db/id row)
                                                     cell)))
                                    :cells     cells})]))]
       [:div.mt-6
        (cc/button {:icon             i/plus
                    :icon-description (tr [:form/add-new-row])
                    :kind             :tertiary
                    :size             :small
                    :disabled         (or preview?
                                          (form-util/update-form-disabled? ctx attr))
                    :on-click         (if preview?
                                        (fn [_])
                                        #(>evt [::form-util/update-form-field
                                                (:target-id ctx)
                                                {:form-id       (:form-id ctx)
                                                 :field-key     (:field attr)
                                                 :form-field-id (:db/id field)}]))}
                   (tr [:form/add-new-row]))]])))


(defn- row-visible? [ctx key current-id select-rows [row-id row]]
  (or (nil? select-rows) ; A static table, all rows are visible
      (<sub [::table-row-added? key row-id]) ; Show (empty) rows added by user
      (->> row ; Show any row if it has non-empty values
           flatten
           (keep #(when (map? %) (:field %)))
           (some #(when-let [value (:form.field/value (child-of current-id % ctx))]
                    (not (and (string? value)
                              (str/blank? value))))))))


(defmethod render :structured-list [ctx key-idx current-id [_ {:keys [class-name headers] :as attr} & children]]
  (let [key (str current-id "/structured-list:" key-idx)]
    [:section.bx--structured-list {:key   key
                                   :class [class-name
                                           (when (-> attr :diff)
                                             (front-form-util/changes-class (-> attr :diff :type)))]}
     [:div.bx--structured-list-thead
      [:div.bx--structured-list-row.bx--structured-list-row--header-row
       (for-indexed [[header-idx header] headers]
         [:div.bx--structured-list-th {:key (str header-idx key)}
          header])]]
     [:div.bx--structured-list-tbody
      (for-all-indexed [[row-id children-row] children]
        [:div.bx--structured-list-row {:key (str key row-id)}
         (for-all [[cell-idx cell] (map-indexed vector children-row)
                   :let [cell-id (str key row-id cell-idx)]]
           [:div.bx--structured-list-td.align-middle {:key cell-id}
            [render-form-element (assoc ctx :table-cell? true) "" current-id cell]])])]]))


(defmethod render :filtered-table [ctx key-idx current-id [_ attr & children]]
  (let [tr            (-> ctx :tr)
        headers       (-> attr :headers)
        class         (-> attr :class-name)
        select-rows   (-> attr :select-rows)
        col-count     (count headers)
        breakpoint    (resp-table/get-breakpoint-by-col-count col-count)
        key           (str current-id "/structured-list:" key-idx)
        rows-with-ids (->> children
                           (map-indexed vector)
                           (filter #(row-visible? ctx key current-id select-rows %)))
        missing-rows  (->> select-rows
                           (map-indexed vector)
                           (remove #(contains? (->> rows-with-ids (map first) set)
                                               (first %))))
        table-opts    (util/mmap breakpoint col-count)]
    [:div
     (diff-tool-classes attr)
     (when-not (empty? rows-with-ids)
       [resp-table/table (assoc table-opts :class ["mb-16 lg:mb-12" class])
        (when-not (empty? rows-with-ids)
          [resp-table/header (assoc table-opts :cells headers)])
        (for-frag [[_ cells] rows-with-ids]
          [resp-table/row (merge table-opts
                                 {:headers   headers
                                  :render-fn (partial render-form-element (assoc ctx :table-cell? true) "" current-id)
                                  :cells     cells})])])
     (when-not (empty? missing-rows)
       [:div.contained-select
        (cc/select
          {:id         (dom-id ctx key "add-row-select")
           :label-text (tr [:add-row])
           :value      :none
           :on-change  #(>evt [::add-table-row key (futil/tval %)])}
          (cc/select-item {:value :none
                           :text  (tr [:select-option])})
          (for-frag [[index row-label] missing-rows]
            (cc/select-item {:value index
                             :text  row-label})))])]))


(defmethod render :h2 [_ctx key-idx _ [_ attr content]]
  (when (<sub [::form-subs/field-visible? attr])
    [:div
     (diff-tool-classes attr)
     [:h2.uppercase.mb-2.mt-8.text-sm.leading-tight.font-semibold {:class (:class-name attr)
                                                                   :key   key-idx}
      content]]))


(defmethod render :p [_ctx key-idx _ [_ attr content]]
  (when (<sub [::form-subs/field-visible? attr])
    [:div
     (diff-tool-classes attr)
     (for-frag-indexed [[i line] (str/split content #"\n")]
       [:p.pb-2 {:class (:class-name attr)
                 :key   (str key-idx "-" i)}
        (form-link/text-with-anchors line)])]))


(defmethod render :ul [_ctx key-idx _ [_ attr & children]]
  (when (<sub [::form-subs/field-visible? attr])
    [:div
     (diff-tool-classes attr)
     [:ul {:class ["list-disc list-inside" (:class-name attr)]
           :key   key-idx}
      (for-frag [child-row children]
        [:li.mb-2 child-row])]]))


(defmethod render :span [_ctx key-idx _ [_ attr content]]
  (when (<sub [::form-subs/field-visible? attr])
    [:div
     (diff-tool-classes attr)
     [:span {:class (:class-name attr)
             :key   key-idx}
      (form-link/text-with-anchors content)]]))


(defmethod render :link [_ctx _ _ [_ attr content]]
  (when (<sub [::form-subs/field-visible? attr])
    [:div
     (diff-tool-classes attr)
     (cc/link
       {:href       (str (:href attr))
        :class-name (str (:class-name attr))}
       content)]))


(defmethod render :radio-button-group [ctx key-idx current-id [_ attr & children]]
  (let [form-field (child-of current-id (-> attr :field) ctx)
        ctx        (assoc ctx :form-field form-field)
        id-str     (field-dom-id ctx form-field attr)]
    [:div
     (diff-tool-classes attr)
     [:fieldset
      {:key   (str key-idx)
       :class (str "bx--fieldset" (updated-class (:form-id ctx) (:db/id form-field)))}
      (when-not (:hide-label attr)
        [:legend.bx--label
         {:class (when (-> attr :form.field/required) "required-field")}
         (label-text attr)])
      (into
        (cc/radio-button-group
          (p/assoc-if {:key              id-str
                       :name             id-str
                       :disabled         (form-util/update-form-disabled? ctx attr)
                       :default-selected (str (:default-selected attr))
                       :on-change        #(>evt [::form-util/update-form-field
                                                 (:target-id ctx)
                                                 (update-signal-args ctx
                                                                     form-field
                                                                     (:field attr)
                                                                     %)])}
                      :value-selected (:form.field/value form-field)
                      :class-name (:class-name attr)
                      :orientation (:layout attr)))
        (remove
          nil?
          (for-all [radio-button children]
            (render ctx id-str current-id radio-button))))]]))


(defmethod render :radio-button [ctx key-idx _ [_ attr]]
  (when (<sub [::form-subs/field-visible? attr])
    (let [value (:value attr)]
      (cc/radio-button
        {:key        (str key-idx "." value)
         :name       (str key-idx "." value)
         :label-text (label-text attr)
         :value      value
         :disabled   (form-util/update-form-disabled? ctx attr)}))))


(defmethod render :radio-button-group-custom [ctx key-idx current-id [_ attr & children]]
  (when (<sub [::form-subs/field-visible? attr])
    (when-let [form-field (child-of current-id (-> attr :field) ctx)]
      [:div
       (diff-tool-classes attr)
       [:fieldset
        {:key   (str key-idx)
         :class (str "bx--fieldset" (updated-class (:form-id ctx) (:db/id form-field)))}
        (when-not (:hide-label attr)
          [:legend.bx--label
           {:class (when (-> attr :form.field/required) "required-field")}
           (label-text attr)])
        (into
          [:div.bx--radio-button-group.bx--radio-button-group--label-right.flex.flex-wrap]
          (remove
            nil?
            (for-all [[radio-button props] children]
              [render-form-element
               (assoc ctx ::radio-group-name (field-dom-id ctx form-field attr))
               key-idx
               current-id
               [radio-button (assoc props
                                    :curr-selected (:form.field/value form-field)
                                    :on-change #(>evt [::form-util/update-form-field
                                                       (:target-id ctx)
                                                       (update-signal-args ctx
                                                                           form-field
                                                                           (:field attr)
                                                                           (futil/tval %))]))]])))]])))


(defmethod render :radio-button-custom [ctx _ _ [_ {:keys [id value] :as attr}]]
  (when (<sub [::form-subs/field-visible? attr])
    (ui/radio-button-custom (or (::radio-group-name ctx) (dom-id ctx value))
                            (-> attr
                                (p/assoc-if :id (some->> id (dom-id ctx)))
                                (assoc :disabled (form-util/update-form-disabled? ctx attr)
                                       :content (cond
                                                  (:content attr) (:content attr)
                                                  (:src attr) (image-tag ctx attr)
                                                  :else ""))))))


(defn date-picker-component
  "Separated into own component to allow r/with-let"
  [ctx current-id form-field attr children]
  (r/with-let [*visited? (r/atom false)]
    (let [value                (:form.field/value form-field)
          invalid?             (not (str/blank? (get-invalid-text ctx attr value @*visited?)))
          another-picker-value (-> (child-of current-id (:another-picker-value attr) ctx)
                                   :form.field/value)
          label                (or (when (:label attr) (label-text attr))
                                   ;; Old date-picker schemas had separate date-picker-input child elements
                                   ;; which contained the label
                                   (->> children
                                        (some (fn [[_ attr]]
                                                (label-text attr)))))]
      [:div.bx--form-item
       (diff-tool-classes attr)
       [ui/date-picker {:id          (field-dom-id ctx form-field attr)
                        :width       "100%"
                        :tr          (:tr ctx)
                        :value       value
                        :label-text  label
                        :hide-label  (boolean (or (:table-cell? ctx) (:hide-label attr)))
                        :invalid?    invalid?
                        :required?   (:form.field/required attr)
                        :disabled?   (or (form-util/update-form-disabled? ctx attr)
                                         (:disabled? attr))
                        :placeholder (:placeholder attr) ; Not currently supported by ui/date-picker
                        :class-name  (str (:class-name attr) (updated-class (:form-id ctx) (:db/id form-field)))
                        :min-date    (cond
                                       (and (= (:min-date attr) :another-picker)
                                            (some? (:another-picker-value attr))
                                            (inst? another-picker-value))
                                       another-picker-value

                                       (= (:min-date attr) :today)
                                       (date-util/today-start-of-day))
                        :max-date    (when (and (= (:max-date attr) :another-picker)
                                                (some? (:another-picker-value attr))
                                                (inst? another-picker-value))
                                       another-picker-value)
                        :callback    (fn [d]
                                       ;; TODO start-of-day on group timezone? Now, if user is on a
                                       ;;      different timezone, backend rendering will show potentially
                                       ;;      wrong date
                                       (let [new-date (date-util/start-of-day d)]
                                         (reset! *visited? true)
                                         (>evt [::form-util/update-form-field
                                                (:target-id ctx)
                                                (update-signal-args ctx form-field (:field attr) new-date)])
                                         (when-let [counter-field (and (inst? another-picker-value)
                                                                       (-> current-id
                                                                           (child-of (:counter attr) ctx)))]
                                           (>evt [::form-util/update-form-field
                                                  (:target-id ctx)
                                                  (update-signal-args ctx
                                                                      counter-field
                                                                      (:form.field/id counter-field)
                                                                      (date-util/number-of-days-between-two-dates
                                                                        (if (date-util/on-or-before? new-date
                                                                                                     another-picker-value)
                                                                          new-date
                                                                          another-picker-value)
                                                                        (if (date-util/after? new-date
                                                                                              another-picker-value)
                                                                          new-date
                                                                          another-picker-value)
                                                                        1
                                                                        true))]))))}]])))


(defmethod render :date-picker [ctx _ current-id [_ attr & children]]
  (let [form-field (child-of current-id (-> attr :field) ctx)]
    [date-picker-component ctx current-id form-field attr children]))


(defmethod render :date-counter [ctx _ current-id [_ attr]]
  (when-let [form-field (child-of current-id (-> attr :field) ctx)]
    (when-let [date1 (get (child-of current-id (-> attr :date-value-1) ctx) :form.field/value)]
      (when-let [date2 (get (child-of current-id (-> attr :date-value-2) ctx) :form.field/value)]
        (cc/text-input {:id         (field-dom-id ctx form-field attr)
                        :disabled   (form-util/update-form-disabled? (assoc ctx :form-field form-field) attr)
                        :label-text (:label attr)
                        :class-name (:class-name attr)
                        :value      (if (and (not (str/blank? date1))
                                             (not (str/blank? date2)))
                                      (str (:form.field/value form-field))
                                      "")})))))


(defn time-picker-component [ctx form-field attr]
  (r/with-let [*visited? (r/atom false)]
    (let [value        (or (:form.field/value form-field)
                           [nil nil])
          invalid-text (get-invalid-text ctx attr value @*visited?)]
      [ui/time-input {:id           (field-dom-id ctx form-field attr)
                      :label-text   (:label attr)
                      :class        "mt-2"
                      :on-change    #(rf/dispatch-sync [::form-util/update-form-field
                                                        (:target-id ctx)
                                                        (update-signal-args ctx form-field (:field attr) %)])
                      :value        value
                      :invalid?     (boolean invalid-text)
                      :invalid-text (or invalid-text "")
                      :required?    (-> attr :form.field/required true?)
                      :disabled?    (form-util/update-form-disabled? (assoc ctx :form-field form-field) attr)}])))


(defmethod render :time-picker [ctx _ current-id [_ attr]]
  (let [form-field (child-of current-id (:field attr) ctx)]
    [time-picker-component ctx form-field attr]))


(defmethod render :accordion [ctx _ current-id [_ attr & children]]
  (into (cc/accordion {:class-name (str (:class-name attr))})
        (for-all [accordion-item children]
          [render-form-element ctx "" current-id accordion-item])))


(defmethod render :accordion-item [ctx _ current-id [_ attr & children]]
  (into (cc/accordion-item {:title      (str (:title attr))
                            :class-name (str (:class-name attr))})
        (for-all [child children]
          [render-form-element ctx "" current-id child])))


(defmethod render :information-list [_ _ _ [_ attr & content]]
  [:div {:class-name (:class-name attr)}
   [:h4 {:key (:title attr)} (:title attr)]
   (for [row content]
     [:p {:key row} row])])


;;
;; Signature:
;;


(rf/reg-event-fx
  ::signature-sign-off
  (fn [_ [_ {:keys [target-id field-id form-id field-key]}]]
    {:fx [[:dispatch [::form-subs/capture-opening-values form-id]]
          [:dispatch [:app.front.process/signal
                      target-id
                      [:process.signal/sign-form
                       (p/filter-vals some?
                                      {:form-field-id field-id
                                       :field-key     field-key
                                       :form-id       form-id})]
                      {:on-success  [::form-util/update-form-field.success form-id nil]
                       :track-save? true}]]]}))

(defn signature-label [_ {:keys [label]}]
  (when label
    [:div.bx--row
     [:h2.bx--col-xs-12 label]]))


(defn signature-text [_ {:keys [text]}]
  (when text
    [:div.bx--row.signature__text.leading-tight
     (into
       [:div.bx--col-xs-12.bx--col-md-12.bx--col-sm-6.bx--col-lg-12.bx--col-xl-9.bx--col-xxl-6 {}]
       (for [line (str/split text #"\n")]
         [:p.pb-3 (form-link/text-with-anchors line)]))]))


(defn signature-data [{:keys [tr]} {:keys [first-name last-name signature-inactive ts]}]
  (let [icon-fill-color (if signature-inactive
                          "orange"
                          "green")]
    [:div.bx--col-xs-12.signature__signatures__sig
     [cc/icon {:icon        i/undersign
               :fill        icon-fill-color
               :description (tr [:signature/signed-by])}]
     [:span.ml-2
      (tr [:signature/signed-by])
      " "
      [:span.font-bold
       (-> first-name :form.field/value)
       " "
       (-> last-name :form.field/value)]
      " "
      ((<sub [::date-util/date-time-fn]) (-> ts :form.field/value js/Date.))
      (when signature-inactive
        [:span.font-bold
         " "
         (tr [:signature/signature-inactive])])]]))

(defn signature-signatures [{:keys [signatures] :as ctx} _]
  [:div.bx--row.signature__signatures
   (for-frag [signature signatures]
     [signature-data ctx signature])])


(defn signature-sign [{:keys [target-id tr field form-id]} attr]
  (let [field-id  (-> field :db/id)
        field-key (:field attr)]
    (cc/button {:kind             :primary
                :on-click         #(>evt [::signature-sign-off (util/mmap target-id field-id field-key form-id)])
                :icon             i/undersign
                :disabled         (not (<sub [:app.front.process/signal-permitted?
                                              target-id
                                              [:process.signal/sign-form
                                               {:form-field-id field-id
                                                :form-id       form-id
                                                :field-key     field-key}]]))
                :icon-description (tr [(-> attr :sign-off)])}
               (tr [(-> attr :sign-off)]))))

(defmethod render :signature [{:keys [preview?] :as ctx} _ current-id [_ attr]]
  (when (<sub [::form-subs/field-visible? attr])
    (let [field                (child-of current-id (-> attr :field) ctx)
          signatures           (when field
                                 (->> (<sub [::form-field-children (-> field :db/id)])
                                      (sort-by :form.field/index >)
                                      (mapv (fn [signature-row]
                                              (->> (<sub [::form-field-children (-> signature-row :db/id)])
                                                   (p/index-by :form.field/name))))))
          every-sign-inactive? (every? :signature-inactive signatures)
          ctx                  (assoc ctx
                                      :field field
                                      :signatures signatures)]
      [:div {:class (or (:class-name attr) "signature")
             :key   (-> field :db/id)}
       [signature-label ctx attr]
       [signature-text ctx attr]
       (when (not preview?) [signature-signatures ctx attr])
       (when every-sign-inactive?
         [signature-sign ctx attr])])))

(defmethod render :image [ctx _ _ [_ attr]]
  (when (<sub [::form-subs/field-visible? attr])
    (image-tag ctx attr)))


(defmethod render :application-form [_ _ _ [_ _]]
  ; No front-end implementation
  [:div])


(defmethod render :affidavit-upload [{:keys [tr]} & _]
  [:<>
   [:h2.uppercase.mb-2.mt-8.text-sm.leading-tight.font-semibold (tr [:signature/upload-affidavit])]
   [:div.font-bold "Upload affidavit functionality is not supported for old test applications"]])


(def affidavit-possible-signers #{:role/applicant
                                  :role/property-owner
                                  :role/agent})


(rf/reg-sub ::affidavit-selectable-parties
  (fn [[_ application-id]]
    (rf/subscribe [::entities/entities {:entity/parent application-id
                                        :entity/type   :entity.type/party}]))
  (fn [parties]
    (->> parties
         (filter #(and (party-model/active? %)
                       (-> %
                           party-model/role-keys
                           (set/intersection affidavit-possible-signers)
                           seq)))
         (sort-by party-model/corporation-and-full-name-or-full-name))))


(defn download-affidavit-controls [{:keys [id application-id disabled? tr]}]
  (r/with-let [*selected-party (r/atom nil)]
    (let [lang    (<sub [:language])
          parties (<sub [::affidavit-selectable-parties application-id])]
      [:div.mb-4.flex.items-end.flex-wrap.gap-4
       (if (<sub [::ws-subs/loading-config?])
         [:<> (cc/select-skeleton) (cc/button-skeleton)]
         [:<>
          [ui-select/select {:values       parties
                             :allow-empty? false
                             :required     true
                             :val->str     #(party-model/corporation-and-full-name-or-full-name % lang)
                             :label-text   (tr [:sign-off/signer])
                             :id           id
                             :on-change    (partial reset! *selected-party)
                             :value        @*selected-party}]
          [:div
           (cc/button {:icon             i/download
                       :icon-description (tr [:sign-off/download])
                       :disabled         (or (not @*selected-party) disabled?)
                       :kind             :tertiary
                       :href             (if @*selected-party
                                           (form-util/download-url application-id
                                                                   ;; This mechanism of just picking one of the roles
                                                                   ;; seems buggy, but it seems not to have been
                                                                   ;; a problem in practice.
                                                                   (-> @*selected-party
                                                                       :party/party-roles
                                                                       first
                                                                       :db/id)
                                                                   (:db/id @*selected-party)
                                                                   true
                                                                   true)
                                           "")}
                      (tr [:sign-off/download-affidavit]))]])])))


(defmethod render :affidavit-upload-v2 [{:keys [application-id form tr] :as ctx} _ current-id [_ attr & children]]
  (let [version-of-affidavit-file-exists? (->> form
                                               :form/form-fields
                                               (filter #(-> % :form.field/id (= :affidavit)))
                                               first
                                               form-model/file-input-has-file?)
        update-field-signal               :process.signal/edit-affidavit-document
        ctx                               (assoc ctx :update-field-signal update-field-signal :disabled? false)]
    [:<>
     [:h2.uppercase.mb-2.mt-8.text-sm.leading-tight.font-semibold (tr [:signature/upload-affidavit])]
     [:p.whitespace-pre-line (tr [:sign-off.planning/affidavit-upload-instructions])]
     (if version-of-affidavit-file-exists?
       [render-form-element ctx "" current-id (first children)]
       [:div.flex.flex-row.justify-between.items-end
        [download-affidavit-controls (util/mmap {:id        (dom-id ctx "affidavit-signer")
                                                 :disabled? (form-util/update-form-disabled? ctx attr)}
                                                application-id
                                                tr)]
        [:div.flex.mt-1
         (if (<sub [::ws-subs/loading-config?])
           (cc/button-skeleton) ;; the child element should be a file upload button
           [render-form-element ctx "" current-id (first children)])]])]))


(defn column-labeled-text
  ([label text]
   (column-labeled-text {} label text))
  ([{:keys [class-name text-class-name label-class-name]} label text]
   [:div.flex.flex-col
    {:class (or class-name "bx--col-xs-3 bx--col-md-3")}
    [:h6
     {:class (or label-class-name "text-sm text-gray-700")}
     label]
    [:p.mt-4
     {:class (or text-class-name "leading-tight")}
     text]]))


(defn form-party-data [{:keys [lang tr form] :as ctx}]
  (let [party (:form/party form)]
    [:div.bx--row.mt-2
     [column-labeled-text
      {:text-class-name  "whitespace-pre-line text-sm text-gray-700 leading-tight"
       :label-class-name "font-semibold"}
      (if (<sub [::ws-subs/loading-config?])
        (cc/skeleton-text)
        (party-model/join-roles-as-string lang party))
      (->> [(party-model/simple-name party lang)
            (party-model/get-user-key ctx party :user/street-address)
            (party-model/get-user-key ctx party :user/postal-code)
            (party-model/municipality-province party tr)]
           (remove str/blank?)
           (str/join "\n"))]]))

(defmethod render :form-party-data [ctx _ _ _]
  (if (-> ctx :form :form/party)
    ^{:key (-> ctx :form :form/party :db/id)}
    [form-party-data ctx]
    (js/console.error "No party linked to form" (-> ctx :form :db/id))))
